import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { Sync } from '@mui/icons-material';
import api from '../services/api';

const WordPressSyncButton = ({ site, onSyncComplete, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSync = async () => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await api.post('/auth/wordpress/sync', {
        site_url: site.website_url,
        email: '', // Will be filled from the current user's token
        display_name: '' // Will be filled from the current user's token
      });

      setResult(response.data);
      
      // Auto-redirect to WordPress dashboard if sync successful
      if (response.data.success && response.data.wordpress_response && response.data.wordpress_response.dashboard_url) {
        const dashboardUrl = response.data.wordpress_response.dashboard_url;
        
        // Show success message briefly, then redirect
        setTimeout(() => {
          window.open(dashboardUrl, '_blank');
          handleClose();
        }, 2000);
      }
      
      if (onSyncComplete) {
        onSyncComplete(response.data);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to sync with WordPress');
      console.error('WordPress sync failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setError('');
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Sync />}
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? 'Plugin must be installed to sync with WordPress' : 'Sync with WordPress site'}
        sx={{
          ...(disabled && {
            color: 'text.disabled',
            borderColor: 'text.disabled',
            '& .MuiButton-startIcon': {
              color: 'text.disabled'
            }
          })
        }}
      >
        Login
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Sync with WordPress Site
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will create or update your user account on {site.rootdomain} using your Google OAuth credentials.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Successfully synced with WordPress!
              </Typography>
              {result.wordpress_response && (
                <Box>
                  <Typography variant="body2">
                    User ID: {result.wordpress_response.user_id}
                  </Typography>
                  <Typography variant="body2">
                    Email: {result.wordpress_response.user_email}
                  </Typography>
                  <Typography variant="body2">
                    Display Name: {result.wordpress_response.display_name}
                  </Typography>
                  {result.wordpress_response.dashboard_url && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      🚀 Redirecting to WordPress dashboard in 2 seconds...
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          {!result && !error && (
            <Button
              onClick={handleSync}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Sync />}
            >
              {loading ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WordPressSyncButton;